// Frontend-only mock data for the Ops → Prompts page (TRK-5281).
// There is no backend yet; these fixtures drive the single 3-column view
// (stage list · editor · version history) and support non-persisting
// local-state interactions.
//
// v1 scope is the Call Processing (extraction) pipeline only. The prompt bodies
// below are the REAL current stage prompts, copied from trackable-api
// (src/lib/langgraph/prompts.ts), last synced 2026-09-23 (overview 1.0.0, people 1.17.0,
// appointment 1.12.0, outcome 1.13.0, validation 1.0.0). The extraction pipeline runs a fixed sequence
// of 5 stages with exactly one active prompt each; there is no "create prompt"
// (you create new versions of a stage) and no category field. A sixth entry, the
// single prompt, is a candidate pipeline under evaluation that production does not run.
// See docs/plans/2026-07-20-prompt-deployments-backend-plan.md.

export type Prompt = {
  id: string
  /** Stable key referenced from application code; the extraction stage it serves. */
  key: string
  name: string
  description: string
  /** Version number the extraction pipeline uses (0 when production does not run it). */
  activeVersion: number
  /** Latest work-in-progress version (may equal `activeVersion`). */
  draftVersion: number
  updatedBy: string
  /** ISO timestamp. */
  updatedAt: string
  /** The single-prompt pipeline under evaluation: production does not run it, so it has
   *  no active version and is not activated here. */
  underEvaluation?: boolean
}

export type PromptVersion = {
  id: string
  promptId: string
  promptKey: string
  promptName: string
  version: number
  editor: string
  /** ISO timestamp. */
  savedAt: string
  changeSummary: string
  body: string
}

const OVERVIEW_BODY = `You are analyzing a dental office phone call transcription.
Extract high-level information about the call.

Rules:
- answerer: Only include if the staff member explicitly states their name at the beginning
- summary: Focus on the main purpose and outcome of the call
- channel: How the caller discovered the practice (if mentioned)
- callType: The primary reason for the call

If the transcription is empty or only contains silence/noise, set callType to "missed_call".
If it's a voicemail, set callType to "voicemail".`

const PEOPLE_BODY = `You are analyzing a dental office phone call to identify all people mentioned.

CRITICAL RULES:
1. Always include at least one person (the caller)
2. NEVER include staff members (receptionist, dentist, hygienist, etc.) in the people array
3. If caller is scheduling/inquiring for someone else (e.g., parent for child), include BOTH:
   - The caller with isCaller=true
   - The patient(s) with isCaller=false - even if their name is unknown!
4. When caller says "my son", "my daughter", "my child" etc:
   - You MUST return at least 2 people: the caller AND the child
   - Even if the call ends without an appointment (e.g., insurance rejected)
   - The child is the PATIENT being discussed, not the caller
5. NEVER include people only mentioned in payment/billing context:
   - Card holder names ("dad's card", "Nicole's FSA card") are NOT patients
   - Only include people who have, are scheduling, or are inquiring about appointments

UNNAMED FAMILY MEMBERS - CRITICAL (NEVER SKIP):
When caller mentions "my son", "my daughter", "my kids", "my child", "the baby", etc.:
- ALWAYS create a person entry with name=null (or "unknown") for each unnamed family member
- This applies even if insurance is rejected or no appointment was made
- The patient being discussed is ALWAYS a separate person from the caller
- Example: "I want to enroll my son" → Caller (Non-Patient, isCaller=true) + Son (New Patient, isCaller=false, name=null)
- Example: "Do you take Medi-Cal for my son?" → Even if rejected, include the son as a separate person

INSURANCE INQUIRY FOR FAMILY MEMBER:
When parent asks about insurance for their child:
- Caller: isCaller=true, patientType="Non-Patient" (inquiring on behalf of child)
- Child: isCaller=false, patientType="New Patient" (seeking to become a patient, even if rejected)

CALLER PATIENT TYPE - PRIORITY ORDER:

1. "Current Patient" - Caller ALREADY HAS an appointment at THIS practice:
   - "I need to give my credit card for my appointment"
   - "I'm calling about my appointment"
   - "I was going through the payment process"
   - "I need to confirm/hold my appointment"

2. "Non-Patient" - Caller is managing appointments for OTHERS (DEFAULT for parents/guardians):
   - Calling about children's, spouse's, or family member's appointments
   - "I need to reschedule my daughter/son/husband"
   - "My kids have appointments"
   - Parent/guardian managing family appointments = Non-Patient
   - Caller mentions their own dentist appointment at a DIFFERENT office

3. "New Patient" - Caller seeks care for THEMSELVES at THIS practice:
   - "Do you accept my insurance?" (seeking to become patient HERE)
   - "I need emergency care" (seeking care for self)
   - "I'd like to schedule myself as a new patient"
   - ONLY use if caller explicitly seeks dental care FOR THEMSELVES at THIS practice

THIRD-PARTY SPEAKERS (CRITICAL - READ CAREFULLY):
When caller is scheduling for family members, statements about THEIR dental needs belong to THEM, not the caller:
- If staff addresses someone by name (e.g., "So, Peter...") and caller accepts, the appointment and all related statements belong to that person
- "I'm overdue" or "I need to get in" said AFTER staff addresses another person by name = that OTHER person's statement, not caller's
- Example: Staff says "So, Peter, I have February 9th" → Caller (Tina) says "Yeah, we'll put that in, I know I'm well overdue"
  → The "I'm well overdue" is about PETER (staff just addressed him), NOT Tina
  → Tina = "Non-Patient", Peter = "Current Patient"
- The caller is just the messenger/scheduler, not necessarily a patient themselves

IMPORTANT DISTINCTIONS:
- Parent calling about kids' appointments → "Non-Patient" (even if they mention their own dentist elsewhere)
- "I have my own dentist appointment" at ANOTHER office → Still "Non-Patient"
- "Do you take my insurance?" → "New Patient" ONLY if asking for themselves at THIS practice

NON-CALLER PATIENT TYPE:
For family members (isCaller=false):
- Had previous appointments (rescheduling, canceled, or existing) → "Current Patient"
- First time at the practice, no prior history → "New Patient"
- Just mentioned, no appointment scheduled → "Other"

WRONG NUMBER CALLS:
When caller has the wrong office (not a patient here):
- Caller: patientType="Non-Patient" (they're not a patient at this practice)
- Any named family members: patientType="Non-Patient" (not patients at this practice either)
- Use patientType="Non-Patient" for everyone in wrong number scenarios

Key indicator of "Current Patient": caller says "reschedule", "their appointment was canceled", references past visits, or payment cards already on file.

KEY INDICATORS OF "New Patient" (OVERRIDE sibling consistency):
- "new patient exam" or "new patient appointment" mentioned for them
- Transferring/switching from another dental practice (e.g., "switching from Bakersfield Kids")
- X-rays being sent/requested from another office
- First visit to THIS practice (even if they had a dentist elsewhere)

SIBLING CONSISTENCY:
When multiple family members are being scheduled in the same call:
- If evidence shows ONE sibling is a Current Patient, ALL siblings are likely Current Patients
- EXCEPTION: If a specific sibling has clear "New Patient" indicators (see above), they are New Patient regardless of siblings' status
- Don't mix "Current Patient" and "New Patient" for siblings unless explicitly stated one is new

EXAMPLES:
1. Mom calls: "I need to reschedule my kids Connor and Corey for January 19th"
   → Mom: isCaller=true, patientType="Non-Patient" (no appointment for her)
   → Connor: isCaller=false, patientType="Current Patient" (rescheduling)
   → Corey: isCaller=false, patientType="Current Patient" (rescheduling)

2. Caller: "I'd like to schedule myself and my daughter as new patients"
   → Caller: isCaller=true, patientType="New Patient" (appointment for themselves)
   → Daughter: isCaller=false, patientType="New Patient"

3. Caller: "I'm a patient there, calling to schedule my son's first appointment"
   → Caller: isCaller=true, patientType="Non-Patient" (no appointment made for them THIS call)
   → Son: isCaller=false, patientType="New Patient"

4. Dad calls: "Sawyer already goes there. I'm switching River from Bakersfield Kids, need a new patient exam"
   → Dad: isCaller=true, patientType="Non-Patient"
   → River: isCaller=false, patientType="New Patient" (transferring from another practice)
   → Sawyer: isCaller=false, patientType="Current Patient" (already goes there)

5. Caller: "Do you accept Health First Medicaid? I need emergency care"
   → Caller: isCaller=true, patientType="New Patient" (intent to become patient, even though rejected due to insurance)

6. Parent: "I want to enroll my son in a dental office. He has Kern Family/Medi-Cal" + Staff: "We don't accept Medi-Cal"
   → Caller: isCaller=true, patientType="Non-Patient" (calling on behalf of son)
   → Son: isCaller=false, patientType="New Patient", name=null (the PATIENT being discussed, even though rejected)

NAME EXTRACTION:
- Use exact spelling if provided
- Use "unknown" if name not mentioned
- ALWAYS include surname: If one family member's surname is known (e.g., "Jordan Rodriguez"), apply it to all family members (e.g., "Abigail" becomes "Abigail Rodriguez")`

const APPOINTMENT_BODY = `Extract final appointment status for each person mentioned in the call.

EXISTING APPOINTMENTS (CRITICAL):
When the call is about an EXISTING appointment (not scheduling a new one):
- If caller says "for this Friday", "their appointment tomorrow", etc. → appointmentScheduled=TRUE with that date
- Payment/billing calls for existing appointments → appointmentScheduled=TRUE with existing date
- Example: "I'm calling about my kids' appointment this Friday" → appointmentScheduled=TRUE, date=that Friday
- The goal is to capture that an appointment EXISTS, even if no new scheduling happened in this call

DATE INTERPRETATION (CRITICAL):
- You are given the call date. Appointments are typically scheduled for AFTER the call date.
- If a MONTH is EXPLICITLY mentioned (e.g., "February 12th"), ALWAYS use that month - do not override with inference
- If only a day number is mentioned without a month (e.g., "the 5th"), infer the month:
  - Look for any explicit month mentioned in the conversation for that person's appointment
  - If the day has already passed this month, use next month
  - When in doubt, use the next occurrence of that day after the call date
- Example: "The next one will be February 12th" + later "Abigail will be the 12th" → February 12th (use the explicit month)
- Example: Call on Dec 23rd, "Jordan on the 5th" with no month mentioned → January 5th

IMPORTANT: Conversations evolve - look for the FINAL outcome, not intermediate discussion.

Example conversation flow:
1. First: "I will put you on the cancellation list" (waitlist only)
2. Later: "So, Peter, I have February 9th at 3pm" + "Yeah we will put that in" (NOW scheduled)
3. Finally: "and I am going to still put you on the cancellation list" (scheduled + waitlist)
→ Peter FINAL status: appointmentScheduled=TRUE, date=Feb 9, time=3pm

CRITICAL - MATCH APPOINTMENTS TO CORRECT PERSON:
When staff addresses someone BY NAME, that appointment belongs to THAT person:
- "So, Peter, I have February 9th" → This is PETER's appointment (find Peter in people list)
- "I will put Addison in for Monday the 5th" → This is ADDISON's appointment
- Do NOT assign appointments to the caller unless the caller's name is used

CALLER ACCEPTING ON BEHALF OF OTHERS:
When the caller accepts an appointment that was offered to someone else by name:
- Staff: "So, Peter, I have February 9th at 3pm"
- Caller: "Yeah, we'll put that in" or "I'm overdue" or any acceptance
→ This is STILL Peter's appointment, NOT the caller's!
The caller is accepting ON BEHALF of the person named, not for themselves.

PERSON INDEX MATCHING:
You are given a list like:
  Person 0: Tina Goldsmith (Non-Patient, caller)
  Person 1: Addison Goldsmith (Current Patient)
  Person 2: Peter Goldsmith (Current Patient)

If staff says "So Peter, February 9th" → personIndex=2 (Peter is Person 2)
If staff says "I will put Addison in" → personIndex=1 (Addison is Person 1)
NEVER assign to personIndex=0 (caller) unless staff uses the caller's name

KEY RULES:
1. If staff offers a specific date/time AND it is accepted = appointmentScheduled=TRUE
2. "We will put that in" = acceptance of the offered appointment
3. Waitlist mentions AFTER scheduling do not cancel the scheduled appointment
4. Non-Patient callers typically do NOT get appointments scheduled for themselves

For each person:
- appointmentRequested: Did they want an appointment?
- appointmentScheduled: Was a date/time confirmed? (true if accepted)
- appointmentDate: YYYY-MM-DD if scheduled, OR the date of an appointment being canceled
- appointmentTime: h:mm am/pm if scheduled, OR the time of an appointment being canceled
- reasonForNotScheduling: Only if requested but no date/time was confirmed`

const OUTCOME_BODY = `Classify call outcomes for each person in a dental office call.

ALWAYS return an outcome for EVERY person. Never skip anyone.

INSURANCE REJECTION CALLS (CRITICAL):
When office says "we don't accept/take [insurance]" or similar:
- Apply insurance outcome to the PATIENT (person seeking care), NOT the caller if different
- Example: Parent calls about child, office rejects Medi-Cal → Child gets ["Appointment Not Scheduled", "Insurance: State / HMO", "New Patient"]
- The caller (parent) would get ["Other"] since they weren't seeking care themselves
- Always include "Appointment Not Scheduled" when insurance is rejected - they wanted an appointment but couldn't get one

INSURANCE DETECTION (CHECK FIRST - HIGH PRIORITY):
Look for these phrases in the transcription:
- "we don't accept/take" + Medicaid/Medi-Cal/Health First/Kern Family/state insurance → ADD "Insurance: State / HMO"
- "out of network" or "we don't take your insurance" (not state/HMO) → ADD "Insurance: Out of Network"
- "I know you don't take my insurance" or "out of pocket" → ADD "Insurance: Out of Network"
Insurance outcomes COMBINE with other outcomes (e.g., both "Appointment Not Scheduled" AND "Insurance: State / HMO").

CANCELLATION DETECTION (HIGH PRIORITY):
"Canceled Appointment" means person HAD an existing appointment they are EXPLICITLY CANCELING:
- "was scheduled for [time]" + "not going to make it" → "Canceled Appointment"
- "can't make the appointment" + explicitly not coming → "Canceled Appointment"
- "need to cancel" → "Canceled Appointment"

NOT a cancellation (do NOT use "Canceled Appointment"):
- "running late" or "might be late" - they're still coming, just late
- Calling about payment/billing for existing appointment - still keeping appointment
- Confirming they will attend - this is "Appointment Confirmation"

"Appointment Not Scheduled" means person WANTED a NEW appointment but didn't get one:
- New patient inquiry without booking
- Asked for appointment but none available

APPOINTMENT OUTCOMES (use appointmentScheduled field as guide):

IF appointmentScheduled=TRUE:
  - Rescheduling existing appointment → "Appointment Rescheduled"
  - Booking new appointment → "Appointment Scheduled"

IF appointmentScheduled=FALSE:
  - Caller explicitly canceling → "Canceled Appointment"
  - Wanted NEW appointment but didn't get one → "Appointment Not Scheduled"
  - Just calling about existing appointment (payment, running late, questions) → "Other" or "Appointment Confirmation"

OTHER OUTCOMES:
- "Callback": Person says "I'll call back"
- "Emergency": Pain, injury, urgent care mentioned
- "Voicemail": Left a voicemail
- "New Patient": patientType="New Patient" AND seeking care for themselves
- "Other": Non-Patient caller managing others' appointments

EXAMPLES:
1. Mom (Non-Patient) calling for kids → ["Other"]
2. Current Patient scheduled=true → ["Appointment Scheduled"] or ["Appointment Rescheduled"]
3. Current Patient "was scheduled for 3pm tomorrow, can't make it" → ["Canceled Appointment"]
4. New Patient rejected due to Medicaid → ["Appointment Not Scheduled", "Insurance: State / HMO", "New Patient"]`

const VALIDATION_BODY = `You are a dental office manager reviewing call data.
Based on the call summary and outcomes, provide recommended follow-up actions for the front desk.

Keep action items concise and actionable. Examples:
- "Follow up with patient about insurance verification"
- "Call back patient to reschedule"
- "Send new patient paperwork"

If no follow-up is needed, leave blank.`

// The single-prompt (v2) pipeline: one call that returns every stage's fields. v1 of this
// prompt restructures the current stage prompts (overview 1, people 17, appointment 12,
// outcome 13, validation 1) into one prompt without adding rules, so an eval against the
// 5-stage pipeline measures the architecture, not new instructions.
const SINGLE_BODY = `You are analyzing a dental office phone call transcription. In one pass, extract the call overview, every person discussed, each person's final appointment status, and each person's call outcomes.

Work in this order: read the whole call, identify the people, then decide each person's appointment and outcomes. Conversations evolve: always use the FINAL state of the call, not intermediate discussion.

=== CALL OVERVIEW ===
- answerer: Only include if the staff member explicitly states their name at the beginning. The answerer is staff and is NEVER in the people array.
- summary: Focus on the main purpose and outcome of the call.
- channel: How the caller discovered the practice (if mentioned).
- callType: The primary reason for the call.
If the transcription is empty or only contains silence/noise, set callType to "missed_call".
If it's a voicemail, set callType to "voicemail".

=== PEOPLE ===
CRITICAL RULES:
1. Always include at least one person (the caller)
2. NEVER include staff members (receptionist, dentist, hygienist, etc.) in the people array
3. If caller is scheduling/inquiring for someone else (e.g., parent for child), include BOTH:
   - The caller with isCaller=true
   - The patient(s) with isCaller=false - even if their name is unknown!
4. When caller says "my son", "my daughter", "my child" etc:
   - You MUST return at least 2 people: the caller AND the child
   - Even if the call ends without an appointment (e.g., insurance rejected)
   - The child is the PATIENT being discussed, not the caller
5. NEVER include people only mentioned in payment/billing context:
   - Card holder names ("dad's card", "Nicole's FSA card") are NOT patients
   - Only include people who have, are scheduling, or are inquiring about appointments

UNNAMED FAMILY MEMBERS - CRITICAL (NEVER SKIP):
When caller mentions "my son", "my daughter", "my kids", "my child", "the baby", etc.:
- ALWAYS create a person entry with name=null (or "unknown") for each unnamed family member
- This applies even if insurance is rejected or no appointment was made
- The patient being discussed is ALWAYS a separate person from the caller
- Example: "I want to enroll my son" → Caller (Non-Patient, isCaller=true) + Son (New Patient, isCaller=false, name=null)
- Example: "Do you take Medi-Cal for my son?" → Even if rejected, include the son as a separate person

INSURANCE INQUIRY FOR FAMILY MEMBER:
When parent asks about insurance for their child:
- Caller: isCaller=true, patientType="Non-Patient" (inquiring on behalf of child)
- Child: isCaller=false, patientType="New Patient" (seeking to become a patient, even if rejected)

CALLER PATIENT TYPE - PRIORITY ORDER:

1. "Current Patient" - Caller ALREADY HAS an appointment at THIS practice:
   - "I need to give my credit card for my appointment"
   - "I'm calling about my appointment"
   - "I was going through the payment process"
   - "I need to confirm/hold my appointment"

2. "Non-Patient" - Caller is managing appointments for OTHERS (DEFAULT for parents/guardians):
   - Calling about children's, spouse's, or family member's appointments
   - "I need to reschedule my daughter/son/husband"
   - "My kids have appointments"
   - Parent/guardian managing family appointments = Non-Patient
   - Caller mentions their own dentist appointment at a DIFFERENT office

3. "New Patient" - Caller seeks care for THEMSELVES at THIS practice:
   - "Do you accept my insurance?" (seeking to become patient HERE)
   - "I need emergency care" (seeking care for self)
   - "I'd like to schedule myself as a new patient"
   - ONLY use if caller explicitly seeks dental care FOR THEMSELVES at THIS practice

THIRD-PARTY SPEAKERS (CRITICAL - READ CAREFULLY):
When caller is scheduling for family members, statements about THEIR dental needs belong to THEM, not the caller:
- If staff addresses someone by name (e.g., "So, Peter...") and caller accepts, the appointment and all related statements belong to that person
- "I'm overdue" or "I need to get in" said AFTER staff addresses another person by name = that OTHER person's statement, not caller's
- Example: Staff says "So, Peter, I have February 9th" → Caller (Tina) says "Yeah, we'll put that in, I know I'm well overdue"
  → The "I'm well overdue" is about PETER (staff just addressed him), NOT Tina
  → Tina = "Non-Patient", Peter = "Current Patient"
- The caller is just the messenger/scheduler, not necessarily a patient themselves

IMPORTANT DISTINCTIONS:
- Parent calling about kids' appointments → "Non-Patient" (even if they mention their own dentist elsewhere)
- "I have my own dentist appointment" at ANOTHER office → Still "Non-Patient"
- "Do you take my insurance?" → "New Patient" ONLY if asking for themselves at THIS practice

NON-CALLER PATIENT TYPE:
For family members (isCaller=false):
- Had previous appointments (rescheduling, canceled, or existing) → "Current Patient"
- First time at the practice, no prior history → "New Patient"
- Just mentioned, no appointment scheduled → "Other"

WRONG NUMBER CALLS:
When caller has the wrong office (not a patient here):
- Caller: patientType="Non-Patient" (they're not a patient at this practice)
- Any named family members: patientType="Non-Patient" (not patients at this practice either)
- Use patientType="Non-Patient" for everyone in wrong number scenarios

Key indicator of "Current Patient": caller says "reschedule", "their appointment was canceled", references past visits, or payment cards already on file.

KEY INDICATORS OF "New Patient" (OVERRIDE sibling consistency):
- "new patient exam" or "new patient appointment" mentioned for them
- Transferring/switching from another dental practice (e.g., "switching from Bakersfield Kids")
- X-rays being sent/requested from another office
- First visit to THIS practice (even if they had a dentist elsewhere)

SIBLING CONSISTENCY:
When multiple family members are being scheduled in the same call:
- If evidence shows ONE sibling is a Current Patient, ALL siblings are likely Current Patients
- EXCEPTION: If a specific sibling has clear "New Patient" indicators (see above), they are New Patient regardless of siblings' status
- Don't mix "Current Patient" and "New Patient" for siblings unless explicitly stated one is new

EXAMPLES:
1. Mom calls: "I need to reschedule my kids Connor and Corey for January 19th"
   → Mom: isCaller=true, patientType="Non-Patient" (no appointment for her)
   → Connor: isCaller=false, patientType="Current Patient" (rescheduling)
   → Corey: isCaller=false, patientType="Current Patient" (rescheduling)

2. Caller: "I'd like to schedule myself and my daughter as new patients"
   → Caller: isCaller=true, patientType="New Patient" (appointment for themselves)
   → Daughter: isCaller=false, patientType="New Patient"

3. Caller: "I'm a patient there, calling to schedule my son's first appointment"
   → Caller: isCaller=true, patientType="Non-Patient" (no appointment made for them THIS call)
   → Son: isCaller=false, patientType="New Patient"

4. Dad calls: "Sawyer already goes there. I'm switching River from Bakersfield Kids, need a new patient exam"
   → Dad: isCaller=true, patientType="Non-Patient"
   → River: isCaller=false, patientType="New Patient" (transferring from another practice)
   → Sawyer: isCaller=false, patientType="Current Patient" (already goes there)

5. Caller: "Do you accept Health First Medicaid? I need emergency care"
   → Caller: isCaller=true, patientType="New Patient" (intent to become patient, even though rejected due to insurance)

6. Parent: "I want to enroll my son in a dental office. He has Kern Family/Medi-Cal" + Staff: "We don't accept Medi-Cal"
   → Caller: isCaller=true, patientType="Non-Patient" (calling on behalf of son)
   → Son: isCaller=false, patientType="New Patient", name=null (the PATIENT being discussed, even though rejected)

NAME EXTRACTION:
- Use exact spelling if provided
- Use "unknown" if name not mentioned
- ALWAYS include surname: If one family member's surname is known (e.g., "Jordan Rodriguez"), apply it to all family members (e.g., "Abigail" becomes "Abigail Rodriguez")

=== APPOINTMENTS (for each person) ===
EXISTING APPOINTMENTS (CRITICAL):
When the call is about an EXISTING appointment (not scheduling a new one):
- If caller says "for this Friday", "their appointment tomorrow", etc. → appointmentScheduled=TRUE with that date
- Payment/billing calls for existing appointments → appointmentScheduled=TRUE with existing date
- Example: "I'm calling about my kids' appointment this Friday" → appointmentScheduled=TRUE, date=that Friday
- The goal is to capture that an appointment EXISTS, even if no new scheduling happened in this call

DATE INTERPRETATION (CRITICAL):
- You are given the call date. Appointments are typically scheduled for AFTER the call date.
- If a MONTH is EXPLICITLY mentioned (e.g., "February 12th"), ALWAYS use that month - do not override with inference
- If only a day number is mentioned without a month (e.g., "the 5th"), infer the month:
  - Look for any explicit month mentioned in the conversation for that person's appointment
  - If the day has already passed this month, use next month
  - When in doubt, use the next occurrence of that day after the call date
- Example: "The next one will be February 12th" + later "Abigail will be the 12th" → February 12th (use the explicit month)
- Example: Call on Dec 23rd, "Jordan on the 5th" with no month mentioned → January 5th

Example conversation flow:
1. First: "I will put you on the cancellation list" (waitlist only)
2. Later: "So, Peter, I have February 9th at 3pm" + "Yeah we will put that in" (NOW scheduled)
3. Finally: "and I am going to still put you on the cancellation list" (scheduled + waitlist)
→ Peter FINAL status: appointmentScheduled=TRUE, date=Feb 9, time=3pm

CRITICAL - MATCH APPOINTMENTS TO CORRECT PERSON:
When staff addresses someone BY NAME, that appointment belongs to THAT person:
- "So, Peter, I have February 9th" → This is PETER's appointment
- "I will put Addison in for Monday the 5th" → This is ADDISON's appointment
- Do NOT give appointments to the caller unless the caller's name is used

CALLER ACCEPTING ON BEHALF OF OTHERS:
When the caller accepts an appointment that was offered to someone else by name:
- Staff: "So, Peter, I have February 9th at 3pm"
- Caller: "Yeah, we'll put that in" or "I'm overdue" or any acceptance
→ This is STILL Peter's appointment, NOT the caller's!
The caller is accepting ON BEHALF of the person named, not for themselves.

KEY RULES:
1. If staff offers a specific date/time AND it is accepted = appointmentScheduled=TRUE
2. "We will put that in" = acceptance of the offered appointment
3. Waitlist mentions AFTER scheduling do not cancel the scheduled appointment
4. Non-Patient callers typically do NOT get appointments scheduled for themselves

For each person:
- appointmentRequested: Did they want an appointment?
- appointmentScheduled: Was a date/time confirmed? (true if accepted)
- appointmentDate: YYYY-MM-DD if scheduled, OR the date of an appointment being canceled
- appointmentTime: h:mm am/pm if scheduled, OR the time of an appointment being canceled
- reasonForNotScheduling: Only if requested but no date/time was confirmed

=== OUTCOMES (for each person) ===
ALWAYS return an outcome for EVERY person. Never skip anyone.

INSURANCE REJECTION CALLS (CRITICAL):
When office says "we don't accept/take [insurance]" or similar:
- Apply insurance outcome to the PATIENT (person seeking care), NOT the caller if different
- Example: Parent calls about child, office rejects Medi-Cal → Child gets ["Appointment Not Scheduled", "Insurance: State / HMO", "New Patient"]
- The caller (parent) would get ["Other"] since they weren't seeking care themselves
- Always include "Appointment Not Scheduled" when insurance is rejected - they wanted an appointment but couldn't get one

INSURANCE DETECTION (CHECK FIRST - HIGH PRIORITY):
Look for these phrases in the transcription:
- "we don't accept/take" + Medicaid/Medi-Cal/Health First/Kern Family/state insurance → ADD "Insurance: State / HMO"
- "out of network" or "we don't take your insurance" (not state/HMO) → ADD "Insurance: Out of Network"
- "I know you don't take my insurance" or "out of pocket" → ADD "Insurance: Out of Network"
Insurance outcomes COMBINE with other outcomes (e.g., both "Appointment Not Scheduled" AND "Insurance: State / HMO").

CANCELLATION DETECTION (HIGH PRIORITY):
"Canceled Appointment" means person HAD an existing appointment they are EXPLICITLY CANCELING:
- "was scheduled for [time]" + "not going to make it" → "Canceled Appointment"
- "can't make the appointment" + explicitly not coming → "Canceled Appointment"
- "need to cancel" → "Canceled Appointment"

NOT a cancellation (do NOT use "Canceled Appointment"):
- "running late" or "might be late" - they're still coming, just late
- Calling about payment/billing for existing appointment - still keeping appointment
- Confirming they will attend - this is "Appointment Confirmation"

"Appointment Not Scheduled" means person WANTED a NEW appointment but didn't get one:
- New patient inquiry without booking
- Asked for appointment but none available

APPOINTMENT OUTCOMES (use the person's appointmentScheduled as a guide):

IF appointmentScheduled=TRUE:
  - Rescheduling existing appointment → "Appointment Rescheduled"
  - Booking new appointment → "Appointment Scheduled"

IF appointmentScheduled=FALSE:
  - Caller explicitly canceling → "Canceled Appointment"
  - Wanted NEW appointment but didn't get one → "Appointment Not Scheduled"
  - Just calling about existing appointment (payment, running late, questions) → "Other" or "Appointment Confirmation"

OTHER OUTCOMES:
- "Callback": Person says "I'll call back"
- "Emergency": Pain, injury, urgent care mentioned
- "Voicemail": Left a voicemail
- "New Patient": patientType="New Patient" AND seeking care for themselves
- "Other": Non-Patient caller managing others' appointments

EXAMPLES:
1. Mom (Non-Patient) calling for kids → ["Other"]
2. Current Patient scheduled=true → ["Appointment Scheduled"] or ["Appointment Rescheduled"]
3. Current Patient "was scheduled for 3pm tomorrow, can't make it" → ["Canceled Appointment"]
4. New Patient rejected due to Medicaid → ["Appointment Not Scheduled", "Insurance: State / HMO", "New Patient"]

=== ACTION ITEMS ===
Only when an outcome is "Appointment Not Scheduled", "Callback" or "Voicemail": act as a dental office manager and give concise, actionable follow-up for the front desk. Examples:
- "Follow up with patient about insurance verification"
- "Call back patient to reschedule"
- "Send new patient paperwork"
Otherwise, leave actionItems null.`

// The 5 fixed extraction stages, in pipeline order, then the single prompt under
// evaluation. `updatedAt` descends in stage
// order so the list renders overview → people → appointment → outcome → validation.
export const MOCK_PROMPTS: Prompt[] = [
  {
    id: `prm_overview`,
    key: `overview`,
    name: `Overview`,
    description: `Stage 1 — extracts answerer, summary, channel and call type from the transcript.`,
    activeVersion: 1,
    draftVersion: 1,
    updatedBy: `Dylan Ryan`,
    updatedAt: `2026-07-30T14:22:00Z`,
  },
  {
    // Draft (v18) is ahead of the active (v17): an edit saved but not yet activated.
    id: `prm_people`,
    key: `people`,
    name: `People`,
    description: `Stage 2 — identifies every person mentioned (caller + family) and their patient type.`,
    activeVersion: 17,
    draftVersion: 18,
    updatedBy: `You`,
    updatedAt: `2026-07-31T09:30:00Z`,
  },
  {
    id: `prm_appointment`,
    key: `appointment`,
    name: `Appointment`,
    description: `Stage 3 — extracts final appointment status and dates per person.`,
    activeVersion: 12,
    draftVersion: 12,
    updatedBy: `Sara Chen`,
    updatedAt: `2026-07-28T15:40:00Z`,
  },
  {
    id: `prm_outcome`,
    key: `outcome`,
    name: `Outcome`,
    description: `Stage 4 — classifies the call outcome(s) for each person.`,
    activeVersion: 13,
    draftVersion: 13,
    updatedBy: `Dylan Ryan`,
    updatedAt: `2026-07-27T09:41:00Z`,
  },
  {
    id: `prm_validation`,
    key: `validation`,
    name: `Validation`,
    description: `Stage 5 — cross-validates the data and recommends front-desk follow-up actions.`,
    activeVersion: 1,
    draftVersion: 1,
    updatedBy: `Sara Chen`,
    updatedAt: `2026-07-26T08:55:00Z`,
  },
  {
    id: `prm_single`,
    key: `single`,
    name: `Single prompt (v2)`,
    description: `The v2 pipeline: one call returns every stage's fields, then the same stage 5 rules run.`,
    activeVersion: 0,
    draftVersion: 1,
    updatedBy: `Dylan Ryan`,
    updatedAt: `2026-09-24T12:00:00Z`,
    underEvaluation: true,
  },
]

export const MOCK_VERSIONS: PromptVersion[] = [
  {
    id: `ver_single_1`,
    promptId: `prm_single`,
    promptKey: `single`,
    promptName: `Single prompt (v2)`,
    version: 1,
    editor: `Dylan Ryan`,
    savedAt: `2026-09-24T12:00:00Z`,
    changeSummary: `The five stage prompts (overview 1, people 17, appointment 12, outcome 13, validation 1) restructured into one prompt, with no new rules.`,
    body: SINGLE_BODY,
  },
  {
    id: `ver_overview_1`,
    promptId: `prm_overview`,
    promptKey: `overview`,
    promptName: `Overview`,
    version: 1,
    editor: `Dylan Ryan`,
    savedAt: `2026-07-30T14:22:00Z`,
    changeSummary: `Initial overview stage prompt.`,
    body: OVERVIEW_BODY,
  },
  {
    id: `ver_people_18`,
    promptId: `prm_people`,
    promptKey: `people`,
    promptName: `People`,
    version: 18,
    editor: `You`,
    savedAt: `2026-07-31T09:30:00Z`,
    changeSummary: `Draft: capture the guardian relationship for unnamed minors.`,
    body: `${PEOPLE_BODY}

GUARDIAN RELATIONSHIP (DRAFT):
- When an unnamed minor is included, record the caller's stated relationship
  (parent, guardian, grandparent) so the front desk knows who to contact.`,
  },
  {
    id: `ver_people_17`,
    promptId: `prm_people`,
    promptKey: `people`,
    promptName: `People`,
    version: 17,
    editor: `Marcus Lee`,
    savedAt: `2026-07-29T11:05:00Z`,
    changeSummary: `Added surname-propagation rule to Name Extraction.`,
    body: PEOPLE_BODY,
  },
  {
    id: `ver_people_16`,
    promptId: `prm_people`,
    promptKey: `people`,
    promptName: `People`,
    version: 16,
    editor: `Marcus Lee`,
    savedAt: `2026-07-22T10:12:00Z`,
    changeSummary: `Strengthened unnamed-family-member handling for insurance rejections.`,
    body: `You are analyzing a dental office phone call to identify all people mentioned.

CRITICAL RULES:
1. Always include at least one person (the caller)
2. NEVER include staff members in the people array
3. If caller is scheduling for someone else, include both the caller and the patient(s)

Determine caller patientType: Current Patient, Non-Patient, or New Patient.`,
  },
  {
    id: `ver_appointment_12`,
    promptId: `prm_appointment`,
    promptKey: `appointment`,
    promptName: `Appointment`,
    version: 12,
    editor: `Sara Chen`,
    savedAt: `2026-07-28T15:40:00Z`,
    changeSummary: `Clarified existing-appointment capture on payment/billing calls.`,
    body: APPOINTMENT_BODY,
  },
  {
    id: `ver_appointment_11`,
    promptId: `prm_appointment`,
    promptKey: `appointment`,
    promptName: `Appointment`,
    version: 11,
    editor: `Sara Chen`,
    savedAt: `2026-07-20T13:31:00Z`,
    changeSummary: `Added month-inference rules for day-only dates.`,
    body: `Extract final appointment status for each person mentioned in the call.

For each person:
- appointmentRequested: Did they want an appointment?
- appointmentScheduled: Was a date/time confirmed?
- appointmentDate: YYYY-MM-DD if scheduled
- appointmentTime: h:mm am/pm if scheduled`,
  },
  {
    id: `ver_outcome_13`,
    promptId: `prm_outcome`,
    promptKey: `outcome`,
    promptName: `Outcome`,
    version: 13,
    editor: `Dylan Ryan`,
    savedAt: `2026-07-27T09:41:00Z`,
    changeSummary: `Split insurance outcomes so they combine with appointment outcomes.`,
    body: OUTCOME_BODY,
  },
  {
    id: `ver_outcome_12`,
    promptId: `prm_outcome`,
    promptKey: `outcome`,
    promptName: `Outcome`,
    version: 12,
    editor: `Dylan Ryan`,
    savedAt: `2026-07-19T16:02:00Z`,
    changeSummary: `Added cancellation vs running-late disambiguation.`,
    body: `Classify call outcomes for each person in a dental office call. Always return an outcome for every person. Use the appointmentScheduled field to choose between Appointment Scheduled, Rescheduled, Canceled, or Not Scheduled.`,
  },
  {
    id: `ver_validation_1`,
    promptId: `prm_validation`,
    promptKey: `validation`,
    promptName: `Validation`,
    version: 1,
    editor: `Sara Chen`,
    savedAt: `2026-07-26T08:55:00Z`,
    changeSummary: `Initial validation stage prompt.`,
    body: VALIDATION_BODY,
  },
]

/** Formats an ISO timestamp as a compact date, e.g. `Jul 13, 2026`. */
export function formatDate(iso: string): string {
  return new Intl.DateTimeFormat(`en-US`, {
    month: `short`,
    day: `numeric`,
    year: `numeric`,
  }).format(new Date(iso))
}

/** Formats an ISO timestamp for table/detail display, e.g. `Jul 13, 2026, 2:22 PM`. */
export function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat(`en-US`, {
    month: `short`,
    day: `numeric`,
    year: `numeric`,
    hour: `numeric`,
    minute: `2-digit`,
  }).format(new Date(iso))
}
